// Comprehensive CNC Machining Materials Library
// with cutting data for turning and milling operations

// Stock Shape Definitions
export const stockShapes = {
  'round-bar': { 
    name: 'Round Bar', 
    icon: 'circle',
    fields: ['diameter', 'length'],
    description: 'Cylindrical bar stock'
  },
  'hex-bar': { 
    name: 'Hex Bar', 
    icon: 'hexagon',
    fields: ['acrossFlats', 'length'],
    description: 'Hexagonal bar stock'
  },
  'square-bar': { 
    name: 'Square Bar', 
    icon: 'square',
    fields: ['side', 'length'],
    description: 'Square bar stock'
  },
  'rect-bar': { 
    name: 'Rectangular Bar', 
    icon: 'rectangle',
    fields: ['width', 'height', 'length'],
    description: 'Rectangular/flat bar stock'
  },
  'tube': { 
    name: 'Tube/Pipe', 
    icon: 'circle-dot',
    fields: ['outerDia', 'innerDia', 'length'],
    description: 'Hollow tube or pipe'
  },
  'plate': { 
    name: 'Plate', 
    icon: 'square',
    fields: ['width', 'length', 'thickness'],
    description: 'Flat plate stock'
  },
  'casting': { 
    name: 'Casting/Forging', 
    icon: 'box',
    fields: ['maxDia', 'length'],
    description: 'Pre-formed casting or forging'
  },
};

// Comprehensive Material Categories
export const materialCategories = {
  aluminum: {
    name: 'Aluminum Alloys',
    color: '#a8d4e6',
    description: 'Lightweight, corrosion resistant, excellent machinability'
  },
  steel: {
    name: 'Carbon & Alloy Steels',
    color: '#8b8b8b',
    description: 'High strength, durable, wide range of hardness'
  },
  stainless: {
    name: 'Stainless Steels',
    color: '#c0c0c0',
    description: 'Corrosion resistant, tough, work hardens'
  },
  brass: {
    name: 'Brass & Bronze',
    color: '#d4a84b',
    description: 'Easy machining, good conductivity, decorative'
  },
  copper: {
    name: 'Copper Alloys',
    color: '#b87333',
    description: 'Excellent conductivity, soft, gummy'
  },
  titanium: {
    name: 'Titanium Alloys',
    color: '#878787',
    description: 'High strength-to-weight, heat resistant, challenging to machine'
  },
  plastic: {
    name: 'Plastics & Composites',
    color: '#f5f5f5',
    description: 'Lightweight, corrosion proof, various properties'
  },
  castIron: {
    name: 'Cast Iron',
    color: '#4a4a4a',
    description: 'Good dampening, abrasive, brittle chips'
  },
  inconel: {
    name: 'Nickel Alloys',
    color: '#6b6b6b',
    description: 'Extreme heat resistance, very difficult to machine'
  },
};

// Detailed Material Library with Cutting Data
// All values in Imperial (inches, SFM, IPR for turning, IPM for milling)
export const materialLibrary = {
  // ALUMINUM ALLOYS
  'alu-6061-t6': {
    name: 'Aluminum 6061-T6',
    category: 'aluminum',
    hardness: '95 HB',
    density: '0.098 lb/in³',
    machinability: 'Excellent',
    applications: 'Structural, aerospace, automotive, fixtures',
    turning: {
      roughing: { sfm: [600, 1000], ipr: [0.008, 0.015], doc: [0.080, 0.150] },
      finishing: { sfm: [800, 1200], ipr: [0.003, 0.006], doc: [0.010, 0.030] },
    },
    milling: {
      roughing: { sfm: [600, 1000], ipt: [0.004, 0.008], doc: [0.100, 0.250], woc: 0.5 },
      finishing: { sfm: [800, 1200], ipt: [0.002, 0.004], doc: [0.020, 0.050], woc: 0.25 },
    },
    drilling: { sfm: [300, 500], ipr: [0.004, 0.012] },
    notes: 'Use high speeds, sharp tools. Flood coolant recommended. Can machine dry with air blast.'
  },
  'alu-7075-t6': {
    name: 'Aluminum 7075-T6',
    category: 'aluminum',
    hardness: '150 HB',
    density: '0.101 lb/in³',
    machinability: 'Very Good',
    applications: 'Aerospace, high-stress structural, molds',
    turning: {
      roughing: { sfm: [400, 800], ipr: [0.006, 0.012], doc: [0.060, 0.120] },
      finishing: { sfm: [600, 1000], ipr: [0.002, 0.005], doc: [0.010, 0.025] },
    },
    milling: {
      roughing: { sfm: [400, 800], ipt: [0.003, 0.006], doc: [0.080, 0.200], woc: 0.5 },
      finishing: { sfm: [600, 1000], ipt: [0.002, 0.004], doc: [0.015, 0.040], woc: 0.25 },
    },
    drilling: { sfm: [250, 400], ipr: [0.003, 0.010] },
    notes: 'Harder than 6061. May require slightly lower speeds. Good chip formation.'
  },
  'alu-2024-t3': {
    name: 'Aluminum 2024-T3',
    category: 'aluminum',
    hardness: '120 HB',
    density: '0.100 lb/in³',
    machinability: 'Good',
    applications: 'Aircraft structures, truck wheels, fasteners',
    turning: {
      roughing: { sfm: [500, 900], ipr: [0.006, 0.012], doc: [0.060, 0.120] },
      finishing: { sfm: [700, 1100], ipr: [0.002, 0.005], doc: [0.010, 0.025] },
    },
    milling: {
      roughing: { sfm: [500, 900], ipt: [0.003, 0.006], doc: [0.080, 0.180], woc: 0.5 },
      finishing: { sfm: [700, 1100], ipt: [0.002, 0.004], doc: [0.015, 0.040], woc: 0.25 },
    },
    drilling: { sfm: [250, 400], ipr: [0.003, 0.010] },
    notes: 'Good machinability. Susceptible to stress corrosion cracking.'
  },

  // CARBON & ALLOY STEELS
  'steel-1018': {
    name: 'Steel 1018 (Mild Steel)',
    category: 'steel',
    hardness: '126 HB',
    density: '0.284 lb/in³',
    machinability: 'Good',
    applications: 'Shafts, pins, general purpose, weldable',
    turning: {
      roughing: { sfm: [150, 250], ipr: [0.008, 0.015], doc: [0.080, 0.150] },
      finishing: { sfm: [250, 400], ipr: [0.003, 0.006], doc: [0.010, 0.030] },
    },
    milling: {
      roughing: { sfm: [100, 200], ipt: [0.004, 0.008], doc: [0.080, 0.150], woc: 0.4 },
      finishing: { sfm: [200, 350], ipt: [0.002, 0.004], doc: [0.020, 0.050], woc: 0.2 },
    },
    drilling: { sfm: [80, 120], ipr: [0.005, 0.012] },
    notes: 'Low carbon, easy to machine. May produce stringy chips - use chip breaker.'
  },
  'steel-1045': {
    name: 'Steel 1045 (Medium Carbon)',
    category: 'steel',
    hardness: '163-217 HB',
    density: '0.284 lb/in³',
    machinability: 'Good',
    applications: 'Gears, shafts, bolts, studs, axles',
    turning: {
      roughing: { sfm: [120, 200], ipr: [0.008, 0.014], doc: [0.060, 0.120] },
      finishing: { sfm: [200, 350], ipr: [0.003, 0.006], doc: [0.010, 0.025] },
    },
    milling: {
      roughing: { sfm: [80, 150], ipt: [0.004, 0.007], doc: [0.060, 0.120], woc: 0.4 },
      finishing: { sfm: [150, 300], ipt: [0.002, 0.004], doc: [0.015, 0.040], woc: 0.2 },
    },
    drilling: { sfm: [70, 100], ipr: [0.004, 0.010] },
    notes: 'Medium carbon provides good balance of strength and machinability.'
  },
  'steel-4140': {
    name: 'Steel 4140 (Alloy Steel)',
    category: 'steel',
    hardness: '197-255 HB',
    density: '0.284 lb/in³',
    machinability: 'Fair',
    applications: 'High-stress parts, gears, shafts, tools',
    turning: {
      roughing: { sfm: [80, 150], ipr: [0.006, 0.012], doc: [0.050, 0.100] },
      finishing: { sfm: [150, 280], ipr: [0.002, 0.005], doc: [0.008, 0.020] },
    },
    milling: {
      roughing: { sfm: [60, 120], ipt: [0.003, 0.006], doc: [0.050, 0.100], woc: 0.35 },
      finishing: { sfm: [120, 220], ipt: [0.002, 0.004], doc: [0.015, 0.035], woc: 0.2 },
    },
    drilling: { sfm: [50, 80], ipr: [0.003, 0.008] },
    notes: 'Chrome-moly alloy. Requires good coolant. Can be heat treated.'
  },
  'steel-4340': {
    name: 'Steel 4340 (High Strength Alloy)',
    category: 'steel',
    hardness: '217-269 HB',
    density: '0.284 lb/in³',
    machinability: 'Fair',
    applications: 'Aircraft landing gear, power transmission, high-stress',
    turning: {
      roughing: { sfm: [60, 120], ipr: [0.005, 0.010], doc: [0.040, 0.080] },
      finishing: { sfm: [120, 220], ipr: [0.002, 0.004], doc: [0.006, 0.015] },
    },
    milling: {
      roughing: { sfm: [50, 100], ipt: [0.003, 0.005], doc: [0.040, 0.080], woc: 0.3 },
      finishing: { sfm: [100, 180], ipt: [0.001, 0.003], doc: [0.010, 0.025], woc: 0.15 },
    },
    drilling: { sfm: [40, 70], ipr: [0.002, 0.006] },
    notes: 'Very strong alloy. Use coated carbide. Good coolant essential.'
  },
  'steel-12l14': {
    name: 'Steel 12L14 (Free Machining)',
    category: 'steel',
    hardness: '163-197 HB',
    density: '0.284 lb/in³',
    machinability: 'Excellent',
    applications: 'Screw machine parts, high-volume production',
    turning: {
      roughing: { sfm: [200, 350], ipr: [0.010, 0.018], doc: [0.100, 0.180] },
      finishing: { sfm: [350, 500], ipr: [0.004, 0.008], doc: [0.015, 0.035] },
    },
    milling: {
      roughing: { sfm: [150, 300], ipt: [0.005, 0.010], doc: [0.100, 0.180], woc: 0.5 },
      finishing: { sfm: [300, 450], ipt: [0.003, 0.005], doc: [0.020, 0.050], woc: 0.25 },
    },
    drilling: { sfm: [120, 180], ipr: [0.006, 0.015] },
    notes: 'Lead addition for excellent machinability. Best for high-volume screw machine work.'
  },

  // STAINLESS STEELS
  'ss-303': {
    name: 'Stainless 303 (Free Machining)',
    category: 'stainless',
    hardness: '160-190 HB',
    density: '0.286 lb/in³',
    machinability: 'Good (for stainless)',
    applications: 'Fittings, nuts, bolts, screw machine parts',
    turning: {
      roughing: { sfm: [80, 150], ipr: [0.006, 0.012], doc: [0.050, 0.100] },
      finishing: { sfm: [150, 250], ipr: [0.002, 0.005], doc: [0.010, 0.025] },
    },
    milling: {
      roughing: { sfm: [60, 120], ipt: [0.003, 0.006], doc: [0.050, 0.100], woc: 0.35 },
      finishing: { sfm: [120, 200], ipt: [0.002, 0.004], doc: [0.015, 0.035], woc: 0.2 },
    },
    drilling: { sfm: [50, 80], ipr: [0.003, 0.008] },
    notes: 'Best machining stainless. Sulfur addition. Not for welding.'
  },
  'ss-304': {
    name: 'Stainless 304 (18-8)',
    category: 'stainless',
    hardness: '160-190 HB',
    density: '0.289 lb/in³',
    machinability: 'Fair',
    applications: 'Food processing, chemical, general corrosion resistance',
    turning: {
      roughing: { sfm: [60, 120], ipr: [0.005, 0.010], doc: [0.040, 0.080] },
      finishing: { sfm: [120, 200], ipr: [0.002, 0.004], doc: [0.008, 0.020] },
    },
    milling: {
      roughing: { sfm: [50, 100], ipt: [0.003, 0.005], doc: [0.040, 0.080], woc: 0.3 },
      finishing: { sfm: [100, 160], ipt: [0.002, 0.003], doc: [0.015, 0.030], woc: 0.15 },
    },
    drilling: { sfm: [40, 70], ipr: [0.002, 0.006] },
    notes: 'Work hardens quickly. Keep tool cutting, avoid dwelling. Good coolant essential.'
  },
  'ss-316': {
    name: 'Stainless 316 (Marine Grade)',
    category: 'stainless',
    hardness: '149-217 HB',
    density: '0.290 lb/in³',
    machinability: 'Poor',
    applications: 'Marine, chemical, medical implants, harsh environments',
    turning: {
      roughing: { sfm: [50, 100], ipr: [0.004, 0.008], doc: [0.035, 0.070] },
      finishing: { sfm: [100, 180], ipr: [0.002, 0.004], doc: [0.006, 0.015] },
    },
    milling: {
      roughing: { sfm: [40, 80], ipt: [0.002, 0.004], doc: [0.035, 0.070], woc: 0.25 },
      finishing: { sfm: [80, 140], ipt: [0.001, 0.003], doc: [0.012, 0.025], woc: 0.15 },
    },
    drilling: { sfm: [35, 60], ipr: [0.002, 0.005] },
    notes: 'More difficult than 304. Moly content causes gumminess. Use sharp tools.'
  },
  'ss-17-4ph': {
    name: 'Stainless 17-4 PH',
    category: 'stainless',
    hardness: '280-350 HB (aged)',
    density: '0.282 lb/in³',
    machinability: 'Poor',
    applications: 'Aerospace, high-strength corrosion resistant parts',
    turning: {
      roughing: { sfm: [40, 80], ipr: [0.004, 0.008], doc: [0.030, 0.060] },
      finishing: { sfm: [80, 150], ipr: [0.002, 0.004], doc: [0.005, 0.012] },
    },
    milling: {
      roughing: { sfm: [35, 70], ipt: [0.002, 0.004], doc: [0.030, 0.060], woc: 0.25 },
      finishing: { sfm: [70, 120], ipt: [0.001, 0.002], doc: [0.010, 0.020], woc: 0.12 },
    },
    drilling: { sfm: [30, 50], ipr: [0.002, 0.004] },
    notes: 'Precipitation hardening. Machine in solution treated condition if possible.'
  },

  // BRASS & BRONZE
  'brass-360': {
    name: 'Brass 360 (Free Cutting)',
    category: 'brass',
    hardness: '78-90 HRB',
    density: '0.307 lb/in³',
    machinability: 'Excellent',
    applications: 'Fittings, valves, fasteners, screw machine parts',
    turning: {
      roughing: { sfm: [300, 500], ipr: [0.008, 0.015], doc: [0.080, 0.150] },
      finishing: { sfm: [400, 600], ipr: [0.003, 0.006], doc: [0.015, 0.035] },
    },
    milling: {
      roughing: { sfm: [250, 450], ipt: [0.004, 0.008], doc: [0.080, 0.150], woc: 0.45 },
      finishing: { sfm: [400, 550], ipt: [0.002, 0.004], doc: [0.020, 0.040], woc: 0.25 },
    },
    drilling: { sfm: [200, 350], ipr: [0.005, 0.012] },
    notes: 'Best machining brass. Can run dry. Sharp edges, excellent finish.'
  },
  'brass-c260': {
    name: 'Brass C260 (Cartridge)',
    category: 'brass',
    hardness: '65-85 HRB',
    density: '0.308 lb/in³',
    machinability: 'Good',
    applications: 'Electrical, decorative, ammunition casings',
    turning: {
      roughing: { sfm: [250, 400], ipr: [0.006, 0.012], doc: [0.060, 0.120] },
      finishing: { sfm: [350, 500], ipr: [0.003, 0.005], doc: [0.012, 0.030] },
    },
    milling: {
      roughing: { sfm: [200, 350], ipt: [0.003, 0.006], doc: [0.060, 0.120], woc: 0.4 },
      finishing: { sfm: [300, 450], ipt: [0.002, 0.004], doc: [0.018, 0.035], woc: 0.22 },
    },
    drilling: { sfm: [150, 280], ipr: [0.004, 0.010] },
    notes: 'Softer than 360. Good for forming and machining combination.'
  },
  'bronze-c932': {
    name: 'Bronze C932 (Bearing)',
    category: 'brass',
    hardness: '65-75 HRB',
    density: '0.322 lb/in³',
    machinability: 'Good',
    applications: 'Bearings, bushings, wear plates, gears',
    turning: {
      roughing: { sfm: [150, 300], ipr: [0.006, 0.012], doc: [0.050, 0.100] },
      finishing: { sfm: [250, 400], ipr: [0.002, 0.005], doc: [0.010, 0.025] },
    },
    milling: {
      roughing: { sfm: [120, 250], ipt: [0.003, 0.006], doc: [0.050, 0.100], woc: 0.35 },
      finishing: { sfm: [200, 350], ipt: [0.002, 0.004], doc: [0.015, 0.030], woc: 0.2 },
    },
    drilling: { sfm: [100, 200], ipr: [0.003, 0.008] },
    notes: 'High lead content for lubricity. Self-lubricating properties.'
  },

  // COPPER
  'copper-c110': {
    name: 'Copper C110 (ETP)',
    category: 'copper',
    hardness: '40-65 HRB',
    density: '0.323 lb/in³',
    machinability: 'Fair',
    applications: 'Electrical conductors, bus bars, grounding',
    turning: {
      roughing: { sfm: [150, 300], ipr: [0.006, 0.012], doc: [0.050, 0.100] },
      finishing: { sfm: [250, 400], ipr: [0.003, 0.006], doc: [0.010, 0.025] },
    },
    milling: {
      roughing: { sfm: [100, 250], ipt: [0.003, 0.006], doc: [0.050, 0.100], woc: 0.35 },
      finishing: { sfm: [200, 350], ipt: [0.002, 0.004], doc: [0.015, 0.030], woc: 0.2 },
    },
    drilling: { sfm: [100, 180], ipr: [0.003, 0.008] },
    notes: 'Pure copper - very soft and gummy. Use sharp tools, positive rake.'
  },

  // TITANIUM ALLOYS
  'ti-6al4v': {
    name: 'Titanium Ti-6Al-4V (Grade 5)',
    category: 'titanium',
    hardness: '334 HB',
    density: '0.160 lb/in³',
    machinability: 'Difficult',
    applications: 'Aerospace, medical implants, racing, marine',
    turning: {
      roughing: { sfm: [50, 100], ipr: [0.004, 0.008], doc: [0.040, 0.080] },
      finishing: { sfm: [80, 150], ipr: [0.002, 0.004], doc: [0.008, 0.020] },
    },
    milling: {
      roughing: { sfm: [40, 80], ipt: [0.002, 0.004], doc: [0.030, 0.060], woc: 0.25 },
      finishing: { sfm: [60, 120], ipt: [0.001, 0.003], doc: [0.010, 0.025], woc: 0.15 },
    },
    drilling: { sfm: [30, 60], ipr: [0.002, 0.005] },
    notes: 'Low thermal conductivity - heat stays in cut. Use high-pressure coolant. Keep tool cutting!'
  },
  'ti-cp-grade2': {
    name: 'Titanium CP Grade 2',
    category: 'titanium',
    hardness: '200 HB',
    density: '0.163 lb/in³',
    machinability: 'Fair',
    applications: 'Chemical processing, marine, medical',
    turning: {
      roughing: { sfm: [70, 140], ipr: [0.005, 0.010], doc: [0.050, 0.100] },
      finishing: { sfm: [120, 200], ipr: [0.002, 0.005], doc: [0.010, 0.025] },
    },
    milling: {
      roughing: { sfm: [60, 120], ipt: [0.003, 0.005], doc: [0.040, 0.080], woc: 0.3 },
      finishing: { sfm: [100, 160], ipt: [0.002, 0.003], doc: [0.015, 0.030], woc: 0.18 },
    },
    drilling: { sfm: [50, 90], ipr: [0.003, 0.006] },
    notes: 'Commercially pure - easier than alloys. Still requires care and good coolant.'
  },

  // PLASTICS
  'delrin': {
    name: 'Delrin/Acetal (POM)',
    category: 'plastic',
    hardness: 'R120 (Rockwell M)',
    density: '0.052 lb/in³',
    machinability: 'Excellent',
    applications: 'Gears, bearings, bushings, insulators',
    turning: {
      roughing: { sfm: [400, 800], ipr: [0.010, 0.020], doc: [0.100, 0.200] },
      finishing: { sfm: [600, 1000], ipr: [0.005, 0.010], doc: [0.020, 0.050] },
    },
    milling: {
      roughing: { sfm: [350, 700], ipt: [0.005, 0.010], doc: [0.100, 0.200], woc: 0.5 },
      finishing: { sfm: [500, 900], ipt: [0.003, 0.006], doc: [0.025, 0.060], woc: 0.3 },
    },
    drilling: { sfm: [200, 400], ipr: [0.006, 0.015] },
    notes: 'Self-lubricating. Use sharp tools. Air blast cooling preferred.'
  },
  'nylon': {
    name: 'Nylon 6/6',
    category: 'plastic',
    hardness: 'R118 (Rockwell M)',
    density: '0.041 lb/in³',
    machinability: 'Good',
    applications: 'Gears, bearings, insulators, wear parts',
    turning: {
      roughing: { sfm: [300, 600], ipr: [0.008, 0.016], doc: [0.080, 0.160] },
      finishing: { sfm: [500, 800], ipr: [0.004, 0.008], doc: [0.015, 0.040] },
    },
    milling: {
      roughing: { sfm: [250, 550], ipt: [0.004, 0.008], doc: [0.080, 0.160], woc: 0.45 },
      finishing: { sfm: [400, 700], ipt: [0.002, 0.005], doc: [0.020, 0.050], woc: 0.25 },
    },
    drilling: { sfm: [150, 350], ipr: [0.005, 0.012] },
    notes: 'Absorbs moisture - may swell. Flexible, can deflect under cutting pressure.'
  },
  'hdpe': {
    name: 'HDPE (High Density Polyethylene)',
    category: 'plastic',
    hardness: 'R50-80 (Shore D)',
    density: '0.035 lb/in³',
    machinability: 'Good',
    applications: 'Chemical tanks, cutting boards, low friction parts',
    turning: {
      roughing: { sfm: [400, 800], ipr: [0.010, 0.020], doc: [0.100, 0.200] },
      finishing: { sfm: [600, 1000], ipr: [0.005, 0.010], doc: [0.020, 0.050] },
    },
    milling: {
      roughing: { sfm: [350, 700], ipt: [0.005, 0.010], doc: [0.100, 0.200], woc: 0.5 },
      finishing: { sfm: [500, 900], ipt: [0.003, 0.006], doc: [0.025, 0.060], woc: 0.3 },
    },
    drilling: { sfm: [200, 400], ipr: [0.006, 0.015] },
    notes: 'Very soft and flexible. May melt if too slow or dull tool.'
  },

  // CAST IRON
  'cast-iron-gray': {
    name: 'Gray Cast Iron (Class 30)',
    category: 'castIron',
    hardness: '180-220 HB',
    density: '0.260 lb/in³',
    machinability: 'Good',
    applications: 'Machine bases, housings, cylinder blocks',
    turning: {
      roughing: { sfm: [80, 150], ipr: [0.010, 0.018], doc: [0.080, 0.150] },
      finishing: { sfm: [150, 280], ipr: [0.004, 0.008], doc: [0.015, 0.035] },
    },
    milling: {
      roughing: { sfm: [70, 130], ipt: [0.005, 0.010], doc: [0.080, 0.150], woc: 0.4 },
      finishing: { sfm: [120, 220], ipt: [0.003, 0.005], doc: [0.020, 0.045], woc: 0.22 },
    },
    drilling: { sfm: [60, 100], ipr: [0.006, 0.014] },
    notes: 'Abrasive due to graphite. Brittle chips. Usually machined dry.'
  },
  'cast-iron-ductile': {
    name: 'Ductile Iron (65-45-12)',
    category: 'castIron',
    hardness: '170-241 HB',
    density: '0.256 lb/in³',
    machinability: 'Fair',
    applications: 'Gears, crankshafts, high-strength castings',
    turning: {
      roughing: { sfm: [70, 130], ipr: [0.008, 0.015], doc: [0.060, 0.120] },
      finishing: { sfm: [130, 240], ipr: [0.003, 0.006], doc: [0.012, 0.028] },
    },
    milling: {
      roughing: { sfm: [60, 110], ipt: [0.004, 0.008], doc: [0.060, 0.120], woc: 0.35 },
      finishing: { sfm: [100, 180], ipt: [0.002, 0.004], doc: [0.018, 0.038], woc: 0.2 },
    },
    drilling: { sfm: [50, 90], ipr: [0.005, 0.012] },
    notes: 'Stronger than gray iron. Spherical graphite. Can use coolant.'
  },

  // NICKEL ALLOYS
  'inconel-718': {
    name: 'Inconel 718',
    category: 'inconel',
    hardness: '331-375 HB (aged)',
    density: '0.297 lb/in³',
    machinability: 'Very Difficult',
    applications: 'Jet engines, gas turbines, extreme heat applications',
    turning: {
      roughing: { sfm: [30, 60], ipr: [0.004, 0.008], doc: [0.030, 0.060] },
      finishing: { sfm: [60, 100], ipr: [0.002, 0.004], doc: [0.005, 0.015] },
    },
    milling: {
      roughing: { sfm: [25, 50], ipt: [0.002, 0.004], doc: [0.025, 0.050], woc: 0.2 },
      finishing: { sfm: [45, 80], ipt: [0.001, 0.002], doc: [0.008, 0.020], woc: 0.12 },
    },
    drilling: { sfm: [20, 40], ipr: [0.001, 0.003] },
    notes: 'Extreme work hardening. Ceramic or CBN tools. High-pressure coolant essential.'
  },
  
  // FLAT/TOOL STEELS
  'steel-a36-flat': {
    name: 'A36 Flat Steel (Hot Rolled)',
    category: 'steel',
    hardness: '119-159 HB',
    density: '0.284 lb/in³',
    machinability: 'Good',
    applications: 'Structural steel, brackets, plates, fixtures',
    turning: {
      roughing: { sfm: [120, 200], ipr: [0.008, 0.014], doc: [0.080, 0.140] },
      finishing: { sfm: [200, 320], ipr: [0.003, 0.006], doc: [0.010, 0.028] },
    },
    milling: {
      roughing: { sfm: [100, 180], ipt: [0.004, 0.008], doc: [0.080, 0.150], woc: 0.4 },
      finishing: { sfm: [160, 280], ipt: [0.002, 0.004], doc: [0.020, 0.045], woc: 0.2 },
    },
    drilling: { sfm: [70, 110], ipr: [0.005, 0.012] },
    notes: 'Common structural steel. Scale on surface may be abrasive. Face off scale first.'
  },
  'steel-o1-tool': {
    name: 'O1 Tool Steel (Oil Hardening)',
    category: 'steel',
    hardness: '183-212 HB (annealed)',
    density: '0.283 lb/in³',
    machinability: 'Good (annealed)',
    applications: 'Dies, punches, cutting tools, gauges',
    turning: {
      roughing: { sfm: [80, 140], ipr: [0.006, 0.010], doc: [0.050, 0.100] },
      finishing: { sfm: [140, 240], ipr: [0.002, 0.004], doc: [0.008, 0.020] },
    },
    milling: {
      roughing: { sfm: [70, 120], ipt: [0.003, 0.006], doc: [0.050, 0.100], woc: 0.35 },
      finishing: { sfm: [110, 200], ipt: [0.002, 0.004], doc: [0.015, 0.035], woc: 0.18 },
    },
    drilling: { sfm: [50, 80], ipr: [0.003, 0.008] },
    notes: 'Machine in annealed state. Heat treats to 58-62 HRC. Low distortion.'
  },
  'steel-p20-mold': {
    name: 'P20 Mold Steel (Pre-Hardened)',
    category: 'steel',
    hardness: '277-321 HB (28-34 HRC)',
    density: '0.283 lb/in³',
    machinability: 'Fair',
    applications: 'Plastic injection molds, die holders, large tooling',
    turning: {
      roughing: { sfm: [80, 140], ipr: [0.005, 0.010], doc: [0.050, 0.100] },
      finishing: { sfm: [140, 220], ipr: [0.002, 0.005], doc: [0.008, 0.020] },
    },
    milling: {
      roughing: { sfm: [70, 120], ipt: [0.003, 0.006], doc: [0.050, 0.100], woc: 0.35 },
      finishing: { sfm: [110, 180], ipt: [0.002, 0.004], doc: [0.015, 0.035], woc: 0.18 },
    },
    drilling: { sfm: [50, 85], ipr: [0.003, 0.008] },
    notes: 'Pre-hardened mold steel. No heat treatment needed. Good polishability.'
  },
  'cast-mold-epoxy': {
    name: 'Cast Mold (Epoxy/Tooling Board)',
    category: 'plastic',
    hardness: '75-85 Shore D',
    density: '0.043-0.054 lb/in³',
    machinability: 'Excellent',
    applications: 'Prototype molds, patterns, fixtures, models',
    turning: {
      roughing: { sfm: [600, 1000], ipr: [0.012, 0.022], doc: [0.150, 0.300] },
      finishing: { sfm: [800, 1200], ipr: [0.006, 0.012], doc: [0.030, 0.080] },
    },
    milling: {
      roughing: { sfm: [500, 900], ipt: [0.006, 0.012], doc: [0.150, 0.300], woc: 0.6 },
      finishing: { sfm: [700, 1100], ipt: [0.004, 0.008], doc: [0.040, 0.100], woc: 0.35 },
    },
    drilling: { sfm: [300, 600], ipr: [0.008, 0.020] },
    notes: 'Very easy to machine. Dust can be irritant. Use vacuum/dust collection.'
  },
  'cast-alu-356': {
    name: 'Aluminum Casting A356',
    category: 'aluminum',
    hardness: '70-95 HB',
    density: '0.097 lb/in³',
    machinability: 'Good',
    applications: 'Automotive wheels, aerospace castings, structural',
    turning: {
      roughing: { sfm: [400, 700], ipr: [0.008, 0.015], doc: [0.080, 0.150] },
      finishing: { sfm: [600, 1000], ipr: [0.003, 0.006], doc: [0.010, 0.030] },
    },
    milling: {
      roughing: { sfm: [350, 650], ipt: [0.004, 0.008], doc: [0.080, 0.180], woc: 0.45 },
      finishing: { sfm: [550, 900], ipt: [0.002, 0.004], doc: [0.018, 0.045], woc: 0.22 },
    },
    drilling: { sfm: [200, 400], ipr: [0.004, 0.012] },
    notes: 'Cast aluminum. Silicon content can be abrasive. PCD tools for high volume.'
  },
};

// Helper function to get cutting parameters for a material
export const getCuttingParams = (materialKey, operation, machineType = 'turning') => {
  const material = materialLibrary[materialKey];
  if (!material) return null;
  
  const params = machineType === 'turning' ? material.turning : material.milling;
  if (!params || !params[operation]) return null;
  
  const op = params[operation];
  return {
    sfm: (op.sfm[0] + op.sfm[1]) / 2,
    sfmRange: op.sfm,
    feed: machineType === 'turning' 
      ? (op.ipr[0] + op.ipr[1]) / 2 
      : (op.ipt[0] + op.ipt[1]) / 2,
    feedRange: machineType === 'turning' ? op.ipr : op.ipt,
    doc: (op.doc[0] + op.doc[1]) / 2,
    docRange: op.doc,
  };
};

// Calculate RPM from SFM and diameter
export const calculateRPM = (sfm, diameter) => {
  return Math.round((sfm * 12) / (Math.PI * diameter));
};

// Calculate feed rate
export const calculateFeed = (rpm, feedPerRev) => {
  return rpm * feedPerRev;
};

export default materialLibrary;
